import React, { Fragment, useRef, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Input } from 'cd-eft/components/common/Input';
import { SoilStrengthFormVM } from './SoilStrengthFormVM';
import { WaterwaySizingCalculatorVM } from '../WaterwaySizingCalculatorVM'
import { RadioGroup } from 'cd-eft/components/common/RadioGroup';
import stores from '../../../../models/Stores'
import 'uswds/dist/css/uswds.css';
import './SoilStrengthForm.scss';
import { ComboBox } from 'cd-eft/components/common/ComboBox';


interface ISubFormProps {
  vm: SoilStrengthFormVM;
}
interface ISoilFormProps {
  vm: WaterwaySizingCalculatorVM;
}

const waterwayDesignStore = stores.AppStore.waterwayDesignStore;

const ErodibilityForm = observer((props: ISubFormProps) => {
  return (
    <Fragment>
      <ComboBox
        idAndName="erodibility_dropdown"
        label="Erodibility"
        aria-label="Erodibility"
        valid={!props.vm.formValidation.erodibilityForm.erodibility}
        value={props.vm.erodibilityForm.erodibility}
        onChange={props.vm.handleErodibilityInputChange}
        onBlur={props.vm.handleOnBlurErodibility}
        options={props.vm.erodibilityOptions}
        errorMsg={''}
        required={true}
        className={props.vm.erodabilityClassName}
      />
    </Fragment>
  );
})

const SoilParameterForm = observer((props: ISubFormProps) => {
  return (
    <Fragment>
      <div className="row">
        <div className="col">
          <ComboBox
            idAndName="soil_type"
            label="Soil Type"
            valid={!props.vm.formValidation.soilParameterForm.soilType}
            value={props.vm.soilParameterForm.soilType}
            onChange={props.vm.handleSoilParameterInputChange}
            options={props.vm.soilTypeOptions}
            errorMsg={''}
            required={true}
            className={props.vm.soilParameterFormClassName.soilType}
          />
        </div>
        <div className="col">
          <Input
            idAndName="void_ratio"
            label="Void Ratio"
            valid={!props.vm.formValidation.soilParameterForm.voidRatio}
            value={props.vm.soilParameterForm.voidRatio}
            type="number"
            onChange={props.vm.handleSoilParameterInputChange}
            onBlur={props.vm.handleOnBlurSoilParameter}
            onKeyDown={props.vm.handleOnKeyDown}
            required={true}
            step={'0.01'}
            className={props.vm.soilParameterFormClassName.voidRatio}
          />
        </div>
      </div>
      <div className="row">
        <div className="col">
          <Input
            idAndName="plasticity_index"
            label="Plasticity Index"
            valid={!props.vm.formValidation.soilParameterForm.plasticityIndex}
            value={props.vm.soilParameterForm.plasticityIndex}
            type="number"
            onChange={props.vm.handleSoilParameterInputChange}
            onBlur={props.vm.handleOnBlurSoilParameter}
            onKeyDown={props.vm.handleOnKeyDown}
            required={true}
            step={'0.01'}
            className={props.vm.soilParameterFormClassName.plasticityIndex}
          />
        </div>
        <div className="col">
          <Input
            idAndName="d75"
            label="d75 (in)"
            valid={!props.vm.formValidation.soilParameterForm.d75}
            value={props.vm.soilParameterForm.d75}
            type="number"
            onChange={props.vm.handleSoilParameterInputChange}
            onBlur={props.vm.handleOnBlurSoilParameter}
            onKeyDown={props.vm.handleOnKeyDown}
            required={true}
            step={'0.01'}
            className={props.vm.soilParameterFormClassName.d75}
          />
        </div>
      </div>
      <div className="row">
        <span className="calculated-box" contentEditable={false}>
          <label className="calculated-label-box" htmlFor="calculated-allowable-stress">
            Allowable Stress:
          </label>
          <input
            id="calculated-allowable-stress"
            aria-label="calculated allowable stress"
            className={props.vm.soilParameterFormClassName.allowableStress}
            type="text"
            aria-readonly="true"
            readOnly
            value={props.vm.soilParameterForm.allowableStress}
          />
        </span>
        <span className="calculated-box" contentEditable={false}>
          <label className="calculated-label-box" htmlFor="calculated-soil-grain-roughness">
            Soil Grain Roughness:
          </label>
          <input
            id="calculated-soil-grain-roughness"
            aria-label="calculated soil grain roughness"
            className="calculated-result-box"
            type="text"
            aria-readonly="true"
            readOnly
            value={props.vm.soilParameterForm.soilGrainRoughness}
          />
        </span>
      </div>
    </Fragment>
  );
})

const SoilStressForm = observer((props: ISubFormProps) => {
  return (
    <Fragment>
      <div className='row'>
        <div className='col'>
          <Input idAndName='allowable_stress'
            label='Allowable Stress (lb/sq.ft)'
            valid={!props.vm.formValidation.soilStressForm.allowableStress}
            value={props.vm.soilStressForm.allowableStress}
            type='number'
            onChange={props.vm.handleSoilStressInputChange}
            onBlur={props.vm.handleOnBlurSoilStress}
            onKeyDown={props.vm.handleOnKeyDown}
            required={true}
            step={'0.001'}
            className={props.vm.soilStressFormClassName.allowableStress}
          />
        </div>
        <div className='col'>
          <Input idAndName='soil_grain_roughness'
            label='Soil Grain Roughness'
            valid={props.vm.formValidation.soilStressForm.soilGrainRoughness}
            value={props.vm.soilStressForm.soilGrainRoughness}
            type='number'
            onChange={props.vm.handleSoilStressInputChange}
            onBlur={props.vm.handleOnBlurSoilStress}
            onKeyDown={props.vm.handleOnKeyDown}
            required={true}
            step={'0.0001'}
            className={props.vm.soilStressFormClassName.soilGrainRoughness}
          />
        </div>
      </div>
    </Fragment>
  );
})



const FormContainer = (props: ISoilFormProps) => {
  const vm = useRef(props.vm.soilStrengthVM).current;
  if (!vm) {
    return <></>
  }
  const itemsForRadio = props.vm.mode === "adminGlobalPreferences" ?           
  [{
    id: 'erodibility',
    name: 'erodibility',
    label: 'Erodibility',
    className: 'control-radio-button usa-radio__input--tile',
    onChange: vm.handleFormSelect,
    checked: vm.erodibilitySelect
  }] : 

  [{
    id: 'erodibility',
    name: 'erodibility',
    label: 'Erodibility',
    className: 'control-radio-button usa-radio__input--tile',
    onChange: vm.handleFormSelect,
    checked: vm.erodibilitySelect
  },
  {
    id: 'soil_parameter',
    name: 'soil_parameter',
    label: 'Soil Parameter',
    className: 'control-radio-button usa-radio__input--tile',
    onChange: vm.handleFormSelect,
    checked: vm.soilParameterSelect
  },
  {
    id: 'soil_stress',
    name: 'soil_stress',
    label: 'Soil Stress',
    className: 'control-radio-button usa-radio__input--tile',
    onChange: vm.handleFormSelect,
    checked: vm.soilStressSelect
  }]

  return (
    <div className='ssf-container col'>
      <h3 className="wsc-section-header" tabIndex={0}>Allowable Soil Stress</h3>
      <div className='row control-radio-group'>
        <RadioGroup labelId='soil-strength-control' items={itemsForRadio} />
      </div>
      {vm.soilFormSelect.type === 'erodibility' ? <ErodibilityForm vm={vm} /> : null}
      {vm.soilFormSelect.type === 'soil_parameter' ? <SoilParameterForm vm={vm} /> : null}
      {vm.soilFormSelect.type === 'soil_stress' ? <SoilStressForm vm={vm} /> : null}
    </div>
  )

}


export const SoilStrengthCalculator = observer(FormContainer);

